import { Controller } from '@hotwired/stimulus';

/**
 * disclosure — an accessible, smooth collapsible section.
 *
 * Drives one expand/collapse region. The initial open/closed state is
 * SERVER-RENDERED (so first paint + Turbo 422 re-renders are correct with no
 * flash): the trigger carries `aria-expanded`, the panel carries `data-open`
 * when open (→ the `data-[open]:grid-rows-[1fr]` height), the chevron carries
 * `rotate-90`, the collapsed inner wrapper is `inert`, and the summary shows
 * only while collapsed. `toggle` flips all of these in lock-step, reading the
 * current state straight from the DOM (`aria-expanded`) — so the controller
 * needs no value and does NOTHING on connect (no open/close flash on a Turbo
 * navigation, no re-fire reconciliation).
 *
 * Smoothness comes entirely from CSS: the `panel` target animates
 * `grid-template-rows: 0fr → 1fr` (transition authored in the markup, with
 * `motion-reduce:transition-none` for reduced-motion). `inert` on the inner
 * wrapper keeps collapsed content out of the tab order AND the a11y tree, so a
 * keyboard / screen-reader user can never land on a clipped field.
 *
 * Targets:
 *   • trigger — the <button> whose `aria-expanded` mirrors the state.
 *   • panel   — the grid wrapper; `data-open` toggles `grid-rows-[1fr]`.
 *   • inner   — the `overflow-hidden` wrapper; `inert` while collapsed.
 *   • chevron — the chevron wrapper; `rotate-90` while open.
 *   • summary — the collapsed-state summary; `hidden` while open.
 *
 * Actions:
 *   • toggle — flip the section open/closed (bound to the trigger's click).
 *
 * @example
 *   <section data-controller="disclosure">
 *     <button type="button" data-disclosure-target="trigger"
 *             data-action="disclosure#toggle"
 *             aria-expanded="false" aria-controls="panel-x">
 *       <span data-disclosure-target="chevron">›</span> Titre
 *       <span data-disclosure-target="summary">Résumé</span>
 *     </button>
 *     <div id="panel-x" data-disclosure-target="panel"
 *          class="grid grid-rows-[0fr] data-[open]:grid-rows-[1fr]
 *                 transition-[grid-template-rows] duration-200 ease-out
 *                 motion-reduce:transition-none">
 *       <div class="overflow-hidden min-h-0" data-disclosure-target="inner" inert>…</div>
 *     </div>
 *   </section>
 */
export default class extends Controller {
  static targets = ['trigger', 'panel', 'inner', 'chevron', 'summary'];

  toggle() {
    const open = this.triggerTarget.getAttribute('aria-expanded') !== 'true';
    this.triggerTarget.setAttribute('aria-expanded', String(open));
    this.panelTarget.toggleAttribute('data-open', open);
    if (this.hasInnerTarget) this.innerTarget.toggleAttribute('inert', !open);
    if (this.hasChevronTarget) this.chevronTarget.classList.toggle('rotate-90', open);
    if (this.hasSummaryTarget) this.summaryTarget.classList.toggle('hidden', open);
  }
}
